import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import RequestKindIcon from './RequestKindIcon';
import { REQUEST_KIND_OPTIONS } from '../../utils/requestKinds';
import './NewRequestPicker.css';

/**
 * The menu behind the tab strip's "+": one row per kind of request — icon and name — in
 * the same panel and row style as the app's context menus (`.ctx-menu-btn`). It was a
 * tile grid with a description line; the owner asked for the quieter, list-shaped menu.
 *
 * Keyboard: opens with the first row focused; arrows move and wrap, Enter / Space pick,
 * Escape and Tab close (it is a portal — Tab would otherwise walk out of it while it
 * stays open). Closes on any mousedown outside; `ignoreRef` is the button that opened it,
 * whose own click handler decides what a click on it means.
 *
 * `x` is the RIGHT edge the menu hangs from (it grows to the left), `y` its top.
 */
export default function NewRequestPicker({ x, y, onPick, onClose, ignoreRef }) {
  const { t } = useTranslation();
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  useLayoutEffect(() => {
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      // right edge under `x`; in an empty strip the button is near the left edge — stay inside
      left: Math.max(8, Math.min(x - rect.width, window.innerWidth - rect.width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - rect.height - 8)),
    });
    panelRef.current.querySelector('button')?.focus();
  }, [x, y]);

  useEffect(() => {
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || ignoreRef?.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose({ returnFocus: true }); return; }
      if (e.key === 'Tab') { e.preventDefault(); onClose({ returnFocus: true }); return; }
      const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[e.key];
      if (!step) return;
      e.preventDefault();
      const rows = [...panelRef.current.querySelectorAll('button')];
      const at = rows.indexOf(document.activeElement);
      rows[(at + step + rows.length) % rows.length]?.focus();
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, ignoreRef]);

  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      aria-label={t('requestKinds.pickerTitle')}
      className="new-request-picker fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {REQUEST_KIND_OPTIONS.map((option) => (
        <button
          key={option.id}
          type="button"
          role="menuitem"
          data-request-option={option.id}
          className="ctx-menu-btn"
          onClick={() => onPick(option.id)}
        >
          {/* one column for every icon, so the names start on one line (the gRPC wordmark is wide) */}
          <span className="new-request-picker__icon"><RequestKindIcon optionId={option.id} size={option.id === 'grpc' ? 9 : 14} /></span>
          <span>{t(option.labelKey)}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
}
